import { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp, Loader2, UploadCloud, X } from "lucide-react";
import { formatBytes, formatDuration } from "../lib/format.js";
import { abortBuildUpload } from "../store/upload-manager.js";
import { useAppDispatch, useAppSelector } from "../store/index.js";
import {
  finishedUploadsCleared,
  uploadCancelled,
  uploadDismissed,
  uploadPercent,
  type UploadJob,
} from "../store/uploads.slice.js";

/** A finished upload states its outcome, then gets out of the way on its own. */
const DONE_LINGER_MS = 8000;

const STATUS_TEXT: Record<UploadJob["status"], string> = {
  uploading: "Mengunggah",
  verifying: "Memverifikasi di server",
  done: "Selesai",
  failed: "Gagal",
  cancelled: "Dibatalkan",
};

function transferRate(job: UploadJob): string {
  const elapsed = (job.finishedAt ?? Date.now()) - job.startedAt;
  if (elapsed < 1000 || job.loadedBytes === 0) return "";
  return `${formatBytes(Math.round(job.loadedBytes / (elapsed / 1000)))}/s`;
}

/**
 * Mounted once by the layout so a transfer keeps reporting while the operator
 * works on another page — the dialog that started it is long closed by then.
 */
export function UploadDock() {
  const jobs = useAppSelector((state) => state.uploads.jobs);
  const dispatch = useAppDispatch();
  const [collapsed, setCollapsed] = useState(false);

  // Re-render on a timer so the rate and elapsed readouts keep moving between
  // progress events, which stall for seconds on a slow uplink.
  const [, setTick] = useState(0);
  const active = jobs.filter((job) => job.status === "uploading" || job.status === "verifying");

  useEffect(() => {
    if (active.length === 0) return;
    const timer = window.setInterval(() => setTick((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [active.length]);

  useEffect(() => {
    const timers = jobs
      .filter((job) => job.status === "done")
      .map((job) =>
        window.setTimeout(
          () => dispatch(uploadDismissed(job.id)),
          Math.max(0, DONE_LINGER_MS - (Date.now() - (job.finishedAt ?? Date.now()))),
        ),
      );
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [jobs, dispatch]);

  if (jobs.length === 0) return null;

  const totalPercent =
    active.length > 0
      ? Math.floor(active.reduce((sum, job) => sum + uploadPercent(job), 0) / active.length)
      : 100;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-[#EAECF0] bg-white shadow-lg">
      <button
        type="button"
        onClick={() => setCollapsed((value) => !value)}
        className="flex w-full items-center gap-2 border-b border-[#EAECF0] bg-[#F6F7F9] px-3.5 py-2.5 text-left"
      >
        {active.length > 0 ? (
          <Loader2 className="size-4 shrink-0 animate-spin text-[#2563EB]" />
        ) : (
          <UploadCloud className="size-4 shrink-0 text-[#667085]" />
        )}
        <span className="flex-1 text-xs font-semibold text-[#101828]">
          {active.length > 0
            ? `Mengunggah ${active.length} biner · ${totalPercent}%`
            : "Unggahan selesai"}
        </span>
        {active.length === 0 && (
          <span
            role="button"
            tabIndex={0}
            onClick={(event) => {
              event.stopPropagation();
              dispatch(finishedUploadsCleared());
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.stopPropagation();
              dispatch(finishedUploadsCleared());
            }}
            className="rounded px-1.5 py-0.5 text-[11px] font-medium text-[#667085] hover:bg-[#EAECF0] hover:text-[#101828]"
          >
            Bersihkan
          </span>
        )}
        {collapsed ? (
          <ChevronUp className="size-4 text-[#667085]" />
        ) : (
          <ChevronDown className="size-4 text-[#667085]" />
        )}
      </button>

      {!collapsed && (
        <ul className="max-h-72 divide-y divide-[#EAECF0] overflow-y-auto">
          {jobs.map((job) => {
            const percent = uploadPercent(job);
            const failed = job.status === "failed";
            const done = job.status === "done";
            const rate = transferRate(job);

            return (
              <li key={job.id} className="px-3.5 py-3">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-mono text-[11px] font-medium text-[#101828]" title={job.filename}>
                      {job.filename}
                    </p>
                    <p className="mt-0.5 text-[10px] text-[#667085]">
                      {job.appSlug} · {job.channel} · v{job.version}
                    </p>
                  </div>

                  {done && <CheckCircle2 className="size-4 shrink-0 text-[#17B26A]" />}
                  {failed && <AlertCircle className="size-4 shrink-0 text-[#F04438]" />}
                  {(job.status === "uploading" || job.status === "verifying") && (
                    <button
                      type="button"
                      onClick={() => {
                        abortBuildUpload(job.id);
                        dispatch(uploadCancelled(job.id));
                      }}
                      className="rounded p-0.5 text-[#98A2B3] hover:bg-[#FEF3F2] hover:text-[#F04438]"
                      title="Batalkan unggahan"
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                  {(done || failed || job.status === "cancelled") && (
                    <button
                      type="button"
                      onClick={() => dispatch(uploadDismissed(job.id))}
                      className="rounded p-0.5 text-[#98A2B3] hover:bg-[#EAECF0] hover:text-[#101828]"
                      title="Tutup"
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                </div>

                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#EAECF0]">
                  <div
                    className={`h-full rounded-full transition-[width] duration-200 ${
                      failed
                        ? "bg-[#F04438]"
                        : done
                          ? "bg-[#17B26A]"
                          : job.status === "cancelled"
                            ? "bg-[#98A2B3]"
                            : "bg-[#2563EB]"
                    }`}
                    style={{ width: `${percent}%` }}
                  />
                </div>

                <div className="mt-1.5 flex items-center justify-between text-[10px] text-[#667085]">
                  <span className={failed ? "text-[#B42318]" : ""}>
                    {failed ? (job.error ?? STATUS_TEXT.failed) : STATUS_TEXT[job.status]}
                    {job.status === "uploading" && ` · ${percent}%`}
                  </span>
                  <span className="font-mono">
                    {job.status === "uploading"
                      ? `${formatBytes(job.loadedBytes)} / ${formatBytes(job.sizeBytes)}${rate ? ` · ${rate}` : ""}`
                      : done
                        ? `${formatBytes(job.sizeBytes)} · ${formatDuration((job.finishedAt ?? 0) - job.startedAt)}`
                        : formatBytes(job.sizeBytes)}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
